// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  input,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CtsGantryTimeZone } from '../../interfaces/cts-gantry-time-zone';
import { CtsGantryTimeZoneService } from '../../services/cts-gantry-time-zone.service';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, combineLatest, map } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  standalone: true,
  imports: [CommonModule, TranslateModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: 'app-cts-gantry-timezone-select',
  templateUrl: './cts-gantry-timezone-select.component.html',
  styleUrl: './cts-gantry-timezone-select.component.scss',
})
export class CtsGantryTimezoneSelectComponent {
  localDeviceOffset = input<number | undefined>(undefined);

  protected readonly timeZoneService = inject(CtsGantryTimeZoneService);
  protected readonly translateService = inject(TranslateService);
  private readonly localOffset = this.translateService.instant(
    'cts.gantry.localOffset',
  );

  protected readonly offsetDescription = computed(() => {
    const gmtValue = this.timeZoneService.secondsToGMTString(
      this.localDeviceOffset(),
    );
    return `${this.localOffset} ${gmtValue}`;
  });

  protected readonly timeZone = toSignal(this.timeZoneService.timeZone());
  protected readonly timeZones = toSignal(this.timeZoneService.timeZones());
  protected readonly selectedeTzDescription = computed(() => {
    const tz = this.timeZone();
    return tz ? this.tzDescription(tz) : this.offsetDescription();
  });

  protected readonly dropdownValue$ = new BehaviorSubject<string>(
    this.offsetDescription(),
  );

  protected readonly isValidInput = toSignal(
    combineLatest({
      timeZones: this.timeZoneService.timeZones(),
      dropdownValue: this.dropdownValue$,
    }).pipe(
      map(({ timeZones, dropdownValue }) => {
        const ddValue = String(dropdownValue).toLocaleLowerCase();
        const localOffsetDescription =
          this.offsetDescription().toLocaleLowerCase();

        const tz = timeZones.find((tz) => {
          const tzDescription = this.tzDescription(tz).toLocaleLowerCase();
          return tzDescription.includes(ddValue);
        });

        return localOffsetDescription.includes(ddValue) || tz !== undefined;
      }),
    ),
  );

  protected onDeviceLocalTimeZoneClick(): void {
    this.timeZoneService.remove();
  }

  protected onTimeZoneClick(tz: CtsGantryTimeZone): void {
    this.timeZoneService.store(tz);
  }

  protected onValueChanged(value: string): void {
    this.dropdownValue$.next(value);
  }

  protected tzDescription(tz: CtsGantryTimeZone): string {
    return `${tz.location},${tz.countryIsoCode} (${tz.offsetDescription})`;
  }
}
